import { useCallback, useMemo, useState } from "react"

import { useMatch, useNavigate } from "react-router-dom"

import { useAuthenticationDispatchContext, useMyUserContext } from "@l2r-front/l2r-auth"
import { useTranslation } from "@l2r-front/l2r-i18n"
import { CloseIcon, DomainRoundedIcon, LogoutIcon, SettingsIcon } from "@l2r-front/l2r-icons"
import { useNetworksStateContext } from "@l2r-front/l2r-networks"
import { PropTypes } from "@l2r-front/l2r-proptypes"
import { useBlockingNavigate } from "@l2r-front/l2r-query"
import { Typography } from "@l2r-front/l2r-ui"

import { MobileModulesSelector } from "../../../../common/components/MobileModulesSelector"
import { I18N_NAMESPACE } from "../../../../common/constants/i18n"
import { l2rApps } from "../../../../common/constants/l2rApps"
import { PARAMETERS_PAGE } from "../../../../common/constants/settings"
import { useGetActiveModules } from "../../../../common/hooks/useGetActiveModules"
import { MobileAccountSettingsMenuItem } from "../../components/MobileAccountSettingsMenuItem"
import { MobileNetworkSelector } from "../../containers/MobileNetworkSelector"

import * as Styled from "./MobileSettingsMainPage.styled"

export const MobileSettingsMainPage = (props) => {

    const {
        changePage,
        onClose,
    } = props

    const { t } = useTranslation(I18N_NAMESPACE)

    const match = useMatch("/:selectedNetwork/:app/:module/*")
    const selectedApp = match?.params?.app
    const selectedModule = match?.params?.module
    const navigate = useNavigate()
    const { logout } = useAuthenticationDispatchContext()
    const blockingNavigate = useBlockingNavigate(() => navigate("/"), logout)
    const [bottomSheetContentState, setBottomSheetContentState] = useState("")
    const appsWithActiveModules = useGetActiveModules()
    const { user } = useMyUserContext()
    const { networks, selectedNetwork } = useNetworksStateContext()

    const closeBottomSheet = useCallback(() => {
        setBottomSheetContentState("")
    }, [])

    const displayedModuleLabel = useMemo(() => {
        const app = l2rApps?.[selectedApp]
        const module = app?.modules?.[selectedModule]
        if (!app || !module) {
            return ""
        }
        return t(app.i18nNamespace, module.label)
    }, [selectedApp, selectedModule, t])

    const moduleIcon = useMemo(() => {
        if (!selectedApp || !selectedModule) {
            return null
        }
        const Icon = l2rApps?.[selectedApp]?.modules?.[selectedModule]?.icon
        return <Icon color="primary" />
    }, [selectedApp, selectedModule])

    const availableModules = useMemo(() => {
        return Object.entries(appsWithActiveModules || {}).reduce((acc, [_, app]) => {
            if (!app.modules) {
                return acc
            }
            const appModules = Object.entries(app.modules || {})
                .map(([_, module]) => module.id)
            return acc.concat(appModules)
        }, [])
    }, [appsWithActiveModules])

    const bottomSheetContent = useMemo(() => {
        switch (bottomSheetContentState) {
            case "networkSelector":
                return <MobileNetworkSelector close={onClose} />
            case "moduleSelector":
                return <MobileModulesSelector close={onClose} />
            default:
                return null
        }
    }, [bottomSheetContentState, onClose])

    return <Styled.Container>
        <div>
            <Styled.IconButton onClick={onClose}>
                <CloseIcon />
            </Styled.IconButton>
        </div>
        <Styled.UserInfos>
            <Styled.Avatar />
            <Styled.UserDetails>
                <Typography variant="H1">{user?.firstName} {user?.lastName?.toUpperCase()}</Typography>
                <Styled.UserEmail variant="Small accent">{user?.email}</Styled.UserEmail>
            </Styled.UserDetails>
            <Styled.Divider />
        </Styled.UserInfos>
        <Styled.SettingsContainer>
            <Styled.SettingCategory>
                {networks?.length ? <>
                    <Styled.SettingCategoryTitle variant="Mobile/Body/Bold">{t(I18N_NAMESPACE, "pages.accountSettings.account")}</Styled.SettingCategoryTitle>
                    <MobileAccountSettingsMenuItem
                        label={t(I18N_NAMESPACE, `pages.accountSettings.${networks?.length > 1 ? "networks" : "network"}`)}
                        icon={<DomainRoundedIcon color="primary" />}
                        onClick={networks?.length > 1 ? () => setBottomSheetContentState("networkSelector") : null}
                        desc={selectedNetwork?.name}
                        showArrow={networks?.length > 1}
                    />
                    {availableModules.length ? <MobileAccountSettingsMenuItem
                        label={t(I18N_NAMESPACE, `pages.accountSettings.${availableModules?.length > 1 ? "modules" : "module"}`)}
                        icon={moduleIcon}
                        onClick={availableModules.length > 1 ? () => setBottomSheetContentState("moduleSelector") : null}
                        desc={displayedModuleLabel}
                        showArrow={availableModules?.length > 1}
                    /> : null}
                </> : null}
            </Styled.SettingCategory>
            <Styled.SettingCategory>
                <Styled.SettingCategoryTitle variant="Mobile/Body/Bold">{t(I18N_NAMESPACE, "pages.accountSettings.moreInfos")}</Styled.SettingCategoryTitle>
                <MobileAccountSettingsMenuItem label={t(I18N_NAMESPACE, "pages.accountSettings.parameters")} icon={<SettingsIcon color="primary" />} onClick={() => changePage(PARAMETERS_PAGE)} />
            </Styled.SettingCategory>
        </Styled.SettingsContainer>
        <Styled.Footer>
            <Styled.LogoutButton variant="contained" startIcon={<LogoutIcon />} onClick={blockingNavigate}>
                {t(I18N_NAMESPACE, "containers.accountMenu.logout")}
            </Styled.LogoutButton>
        </Styled.Footer>
        <Styled.BottomSheet displayPuller open={!!bottomSheetContentState.length} onClose={closeBottomSheet}>
            {bottomSheetContent}
        </Styled.BottomSheet>
    </Styled.Container>
}

MobileSettingsMainPage.propTypes = {
    changePage: PropTypes.func.isRequired,
    onClose: PropTypes.func.isRequired,
}